
import { supabase } from '@/integrations/supabase/client';

export interface Team {
  team_name: string;
  team_logo?: string;
}

export interface Game {
  id: string;
  game_name: string;
  game_date: string;
  game_time: string;
  game_timezone: string;
  venue_id?: string;
  venue_name?: string;
  venue_address?: string;
  sport_id?: number;
  home_team_id?: number;
  away_team_id?: number;
  home_team_label: string;
  away_team_label: string;
  home_team?: Team;
  away_team?: Team;
  game_status: string;
  confirmation_status: string;
  countdown_start_at?: string;
  countdown_end_at?: string;
  minimum_players_required: number;
  game_code?: string;
  forfeit_team?: string;
  forfeit_reason?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const gamesService = {
  // Get upcoming games from the new games table
  async getUpcomingGames(limit: number = 10): Promise<Game[]> {
    console.log('Getting upcoming games...');
    
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('games')
        .select('*')
        .gte('game_date', today)
        .order('game_date', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Error fetching upcoming games:', error);
        return [];
      }

      console.log('Upcoming games:', data);
      return data || [];
    } catch (error) {
      console.error('Error getting upcoming games:', error);
      return [];
    }
  },

  // Get games for a specific venue
  async getGamesForVenue(venueName: string): Promise<Game[]> {
    console.log('Getting games for venue:', venueName);
    
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .eq('venue_name', venueName)
        .order('game_date', { ascending: true });

      if (error) {
        console.error('Error fetching venue games:', error);
        return [];
      }

      console.log('Venue games:', data);
      return data || [];
    } catch (error) {
      console.error('Error getting venue games:', error);
      return [];
    }
  },

  // Request to join a game as a player
  async requestToJoinGame(gameId: string, playerId: string, teamSide: 'home' | 'away' = 'home', playerType: 'supabase' | 'internal' = 'supabase'): Promise<boolean> {
    console.log('🎯 Requesting to join game:', { gameId, playerId, teamSide, playerType });
    
    try {
      // Check if player already requested
      const { data: existingRequest, error: checkError } = await supabase
        .from('game_participants')
        .select('id')
        .eq('game_id', gameId)
        .eq('player_id', playerId);

      if (checkError) {
        console.error('❌ Error checking existing request:', checkError);
        return false;
      }

      if (existingRequest && existingRequest.length > 0) {
        console.log('⚠️ Player already requested to join this game');
        return false;
      }

      console.log('✅ No existing request found, proceeding with join request');

      // Add player request
      const { data, error } = await supabase
        .from('game_participants')
        .insert({
          game_id: gameId,
          player_id: playerId,
          player_type: playerType,
          team_side: teamSide,
          participation_status: 'requested',
          eligibility_status: 'eligible',
          requested_by: playerId
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error inserting game participant:', error);
        console.error('Error details:', { message: error.message, details: error.details, hint: error.hint, code: error.code });
        return false;
      }

      console.log('✅ Successfully requested to join game:', data);
      return true;
    } catch (error) {
      console.error('❌ Unexpected error requesting to join game:', error);
      return false;
    }
  },

  // Get games for user's teams
  async getGamesForUserTeams(userId: string): Promise<Game[]> {
    console.log('Getting games for user teams:', userId);
    
    try {
      // Get all user team associations in parallel
      const [coachTeamsResult, playerTeamsResult, ownedTeamsResult, participantGamesResult] = await Promise.all([
        // Teams where user is a coach
        supabase
          .from('team_coaches')
          .select('team_id')
          .eq('coach_id', userId),
        
        // Teams where user is a player
        supabase
          .from('team_players')
          .select('team_id')
          .eq('user_id', userId),
        
        // Teams where user is the owner/creator
        supabase
          .from('teams')
          .select('team_id')
          .eq('created_by', userId),
        
        // Games where user is directly participating
        supabase
          .from('game_participants')
          .select('game_id')
          .eq('player_id', userId)
          .eq('participation_status', 'confirmed')
      ]);

      // Collect all team IDs from different sources
      const teamIds = new Set<number>();
      
      // Add coach teams
      coachTeamsResult.data?.forEach(t => teamIds.add(t.team_id));
      
      // Add player teams
      playerTeamsResult.data?.forEach(t => teamIds.add(t.team_id));
      
      // Add owned teams
      ownedTeamsResult.data?.forEach(t => teamIds.add(t.team_id));

      const teamIdsArray = Array.from(teamIds);
      console.log('User associated team IDs:', teamIdsArray);

      // Get direct game participations
      const directGameIds = participantGamesResult.data?.map(p => p.game_id) || [];
      console.log('User direct game participations:', directGameIds);

      // If no teams and no direct participations, return empty
      if (teamIdsArray.length === 0 && directGameIds.length === 0) {
        console.log('No team associations or game participations found for user');
        return [];
      }

      // Build query conditions
      const conditions = [];
      
      // Add team-based games condition
      if (teamIdsArray.length > 0) {
        conditions.push(`home_team_id.in.(${teamIdsArray.join(',')})`);
        conditions.push(`away_team_id.in.(${teamIdsArray.join(',')})`);
      }
      
      // Add direct participation condition
      if (directGameIds.length > 0) {
        conditions.push(`id.in.(${directGameIds.join(',')})`);
      }

      // Get games from both tables
      const [gamesResult, venueGamesResult] = await Promise.all([
        // Regular games table
        supabase
          .from('games')
          .select('*')
          .or(conditions.join(','))
          .order('game_date', { ascending: true }),
        
        // Venue games table
        teamIdsArray.length > 0 ? supabase
          .from('venue_games')
          .select('*')
          .or(`home_team_id.in.(${teamIdsArray.join(',')}),away_team_id.in.(${teamIdsArray.join(',')})`)
          .order('game_date', { ascending: true }) : null
      ]);

      let allUserGames: Game[] = [];

      // Add regular games
      if (gamesResult.data) {
        allUserGames.push(...gamesResult.data);
      }

      // Convert and add venue games
      if (venueGamesResult?.data) {
        const convertedVenueGames: Game[] = venueGamesResult.data.map(vGame => ({
          id: vGame.id,
          game_name: vGame.game_title || `Game ${vGame.id}`,
          game_date: vGame.game_date,
          game_time: vGame.game_time,
          game_timezone: 'EST',
          venue_id: vGame.venue_id,
          venue_name: vGame.field_location || 'TBD',
          venue_address: '',
          sport_id: vGame.sport_id || null,
          home_team_id: vGame.home_team_id,
          away_team_id: vGame.away_team_id,
          home_team_label: 'Home Team',
          away_team_label: 'Away Team',
          game_status: vGame.game_status || vGame.status || 'pending',
          confirmation_status: vGame.status || 'pending',
          countdown_start_at: undefined,
          countdown_end_at: undefined,
          minimum_players_required: 1,
          game_code: vGame.game_code,
          forfeit_team: vGame.forfeit_team,
          forfeit_reason: vGame.forfeit_reason,
          created_by: vGame.posted_by || '',
          created_at: vGame.created_at || '',
          updated_at: vGame.updated_at || ''
        }));
        allUserGames.push(...convertedVenueGames);
      }

      // Remove duplicates and sort by date
      const uniqueGames = allUserGames.filter((game, index, self) => 
        self.findIndex(g => g.id === game.id) === index
      );
      
      uniqueGames.sort((a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime());

      console.log('User team games found:', uniqueGames.length);
      return uniqueGames;
    } catch (error) {
      console.error('Error getting games for user teams:', error);
      return [];
    }
  },

  // Get recent completed games
  async getRecentGames(limit: number = 5): Promise<Game[]> {
    console.log('Getting recent games...');
    
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('games')
        .select('*')
        .lt('game_date', today)
        .in('game_status', ['completed', 'forfeited'])
        .order('game_date', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent games:', error);
        return [];
      }

      console.log('Recent games:', data);
      return data || [];
    } catch (error) {
      console.error('Error getting recent games:', error);
      return [];
    }
  },

  // Get all games (for discovery/browsing) - combines games and venue_games tables
  async getAllGames(filters?: {
    sport_id?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<Game[]> {
    console.log('Getting all games with filters:', filters);
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch from both tables in parallel
      const [gamesResult, venueGamesResult] = await Promise.all([
        // Regular games table with team data
        supabase
          .from('games')
          .select(`
            *,
            home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
            away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo)
          `)
          .gte('game_date', today)
          .in('game_status', ['pending', 'confirmed', 'approved'])
          .order('game_date', { ascending: true }),
        
        // Regular venue_games queries with proper left joins
        supabase
          .from('venue_games')
          .select('*')
          .gte('game_date', today)
          .in('status', ['approved', 'confirmed'])
          .order('game_date', { ascending: true })
      ]);

      if (gamesResult.error) {
        console.error('Error fetching games:', gamesResult.error);
      }

      if (venueGamesResult.error) {
        console.error('Error fetching venue games:', venueGamesResult.error);
      }

      const regularGames = gamesResult.data || [];
      const venueGames = venueGamesResult.data || [];

      // Get venue data for venue games
      const venueIds = [...new Set(venueGames.map(vg => vg.venue_id))];
      const venuesData: any = {};
      
      if (venueIds.length > 0) {
        // For venue_games, venue_id is UUID and we need to get venue details
        // Since there's no direct relation, we'll try to find venues by matching
        const { data: venues } = await supabase
          .from('venues')
          .select('*');
        
        // For now, we'll use a default venue name since venue_id types don't match
        venueIds.forEach(id => {
          venuesData[id] = { venue_name: 'Venue', venue_address: '' };
        });
      }

      // Get team data for venue games if needed
      const teamIds = [...new Set([
        ...venueGames.map(vg => vg.home_team_id).filter(Boolean),
        ...venueGames.map(vg => vg.away_team_id).filter(Boolean)
      ])];
      
      const teamsData: any = {};
      if (teamIds.length > 0) {
        const { data: teams } = await supabase
          .from('teams')
          .select('team_id, team_name, team_logo')
          .in('team_id', teamIds);
        
        teams?.forEach(team => {
          teamsData[team.team_id] = team;
        });
      }

      // Convert venue games to Game interface format
      const convertedVenueGames: Game[] = venueGames.map(vGame => {
        // Get team data from the separate query
        const homeTeam = vGame.home_team_id ? teamsData[vGame.home_team_id] : null;
        const awayTeam = vGame.away_team_id ? teamsData[vGame.away_team_id] : null;
        
        return {
          id: vGame.id,
          game_name: vGame.game_title || `${homeTeam?.team_name || 'Home Team'} vs ${awayTeam?.team_name || 'Away Team'}`,
          game_date: vGame.game_date,
          game_time: vGame.game_time,
          game_timezone: 'EST', // Default timezone for venue games
          venue_id: vGame.venue_id,
          venue_name: venuesData[vGame.venue_id]?.venue_name || vGame.field_location || 'TBD',
          venue_address: venuesData[vGame.venue_id]?.venue_address || '',
          sport_id: vGame.sport_id || null,
          home_team_id: vGame.home_team_id,
          away_team_id: vGame.away_team_id,
          home_team_label: homeTeam?.team_name || 'Looking for Home Team',
          away_team_label: awayTeam?.team_name || 'Looking for Away Team',
          home_team: homeTeam,
          away_team: awayTeam,
          game_status: vGame.game_status || vGame.status || 'pending',
          confirmation_status: vGame.status || 'pending',
          countdown_start_at: undefined,
          countdown_end_at: undefined,
          minimum_players_required: 1, // Default for venue games
          game_code: vGame.game_code,
          forfeit_team: vGame.forfeit_team,
          forfeit_reason: vGame.forfeit_reason,
          created_by: vGame.posted_by || '',
          created_at: vGame.created_at || '',
          updated_at: vGame.updated_at || '',
          scorekeeper_needed: vGame.scorekeeper_needed,
          scorekeeper_points: vGame.scorekeeper_points
        };
      });

      // Update regular games with actual team names
      const updatedRegularGames = regularGames.map(game => ({
        ...game,
        home_team_label: game.home_team?.team_name || game.home_team_label || 'Home Team',
        away_team_label: game.away_team?.team_name || game.away_team_label || 'Away Team'
      }));

      // Combine both arrays
      let allGames = [...updatedRegularGames, ...convertedVenueGames];

      // Apply filters
      if (filters?.sport_id) {
        allGames = allGames.filter(game => game.sport_id === filters.sport_id);
      }

      if (filters?.status) {
        allGames = allGames.filter(game => 
          game.game_status === filters.status || game.confirmation_status === filters.status
        );
      }

      if (filters?.date_from) {
        allGames = allGames.filter(game => game.game_date >= filters.date_from!);
      }

      if (filters?.date_to) {
        allGames = allGames.filter(game => game.game_date <= filters.date_to!);
      }

      // Sort by date
      allGames.sort((a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime());

      console.log('All games (combined):', allGames.length, 'games from', regularGames.length, 'regular and', convertedVenueGames.length, 'venue games');
      return allGames;
    } catch (error) {
      console.error('Error getting all games:', error);
      return [];
    }
  }
};
